import { useCallback, useEffect, useState } from 'react';

import AdminPasswordConfirmDialog from '../AdminPasswordConfirmDialog';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { usePortalRefresh } from '../../../contexts/PortalRefreshContext';
import { fetchAdminPurgeCatalog, purgeAdminData } from '../../../services/academiaAdminService';
import { AcademicPanelCard, AcademicPanelListItem } from './AcademicPanelCard';

const PURGE_PHRASE = 'VIDER';

type PurgeCategory = {
  key: string;
  label: string;
  description: string;
  danger: 'medium' | 'high' | 'critical';
};

const DANGER_CLASS: Record<PurgeCategory['danger'], string> = {
  medium: 'border-amber-200 bg-amber-50 text-amber-900',
  high: 'border-orange-200 bg-orange-50 text-orange-950',
  critical: 'border-red-300 bg-red-50 text-red-950',
};

const DANGER_LABEL: Record<PurgeCategory['danger'], string> = {
  medium: 'Modéré',
  high: 'Élevé',
  critical: 'Critique',
};

export default function Module12DataPurgeSection({
  onToast,
}: {
  onToast: (message: string, variant?: 'success' | 'error') => void;
}) {
  const [categories, setCategories] = useState<PurgeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PurgeCategory | null>(null);
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const { refresh: refreshPortalData } = usePortalRefresh();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminPurgeCatalog();
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      onToast(e instanceof Error ? e.message : 'Impossible de charger les catégories.', 'error');
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirmPurge = async (password: string, confirmationPhrase?: string) => {
    if (!selected) return;
    setPurging(true);
    setPurgeError(null);
    try {
      const response = await purgeAdminData({
        category: selected.key,
        password,
        confirmationPhrase,
      });
      const deletedUsers = response?.data?.deletedUsers;
      const apiMessage = response?.message;
      onToast(
        deletedUsers
          ? `« ${selected.label} » : ${deletedUsers} compte(s) supprimé(s).`
          : apiMessage || `« ${selected.label} » a été supprimé avec succès.`,
      );
      refreshPortalData();
      setSelected(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Suppression impossible.';
      setPurgeError(message);
      onToast(message, 'error');
    } finally {
      setPurging(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-950">
        <p className="font-semibold">Zone de suppression massive</p>
        <p className="mt-1 text-xs leading-relaxed">
          Ces opérations sont irréversibles. Votre mot de passe administrateur est requis avant toute
          suppression. Réservé à l&apos;administrateur principal.
        </p>
      </div>

      <AcademicPanelCard
        icon={<LucideSymbolIcon name="delete_forever" tone="primary" size={22} />}
        title="Suppression par lot"
        subtitle="Effacez des catégories entières de données de la plateforme"
      >
        {loading ? (
          <p className="text-sm text-on-surface-variant">Chargement des catégories…</p>
        ) : (
          <ul className="space-y-2">
            {categories.map((category) => (
              <AcademicPanelListItem key={category.key}>
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-slate-900">{category.label}</p>
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${DANGER_CLASS[category.danger]}`}
                      >
                        {DANGER_LABEL[category.danger]}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-on-surface-variant">{category.description}</p>
                  </div>
                  <button
                    type="button"
                    disabled={purging}
                    onClick={() => {
                      setPurgeError(null);
                      setSelected(category);
                    }}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-800 transition hover:bg-red-100 disabled:opacity-50"
                  >
                    <LucideSymbolIcon name="delete_forever" tone="inherit" size={16} />
                    Supprimer
                  </button>
                </div>
              </AcademicPanelListItem>
            ))}
          </ul>
        )}
      </AcademicPanelCard>

      <AdminPasswordConfirmDialog
        open={selected !== null}
        title={selected ? `Supprimer : ${selected.label}` : 'Confirmer la suppression'}
        description={
          selected
            ? `${selected.description} Cette action est définitive.`
            : 'Confirmez avec votre mot de passe.'
        }
        confirmLabel="Supprimer définitivement"
        isConfirming={purging}
        errorMessage={purgeError}
        confirmationPhrase={selected?.key === 'base_complete' ? PURGE_PHRASE : null}
        onCancel={() => {
          if (!purging) {
            setSelected(null);
            setPurgeError(null);
          }
        }}
        onConfirm={(password, confirmationPhrase) => void handleConfirmPurge(password, confirmationPhrase)}
      />
    </section>
  );
}
